import React from "react";
import { motion } from "framer-motion";

function Hero({ title, description, image, imageAlt, badges = [], actions = [] }) {
  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-brand-teal/5 via-white to-white">
      <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-brand-teal/20 blur-3xl" />
      <div className="pointer-events-none absolute top-10 -right-20 h-96 w-96 rounded-full bg-brand-green/20 blur-3xl" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.4]"
        style={{
          backgroundImage:
            "radial-gradient(circle, #0b222411 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      <div className="relative px-5 pt-16 md:pt-24 pb-8 md:pb-12 min-h-[calc(100vh-6rem)] max-w-7xl mx-auto grid lg:grid-cols-[0.55fr_1.45fr] gap-10 lg:gap-12 items-center">
        <div className="text-center lg:text-left order-2 lg:order-1">
          <motion.h1
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-balance text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-brand-dark leading-[1.1]"
          >
            {title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="max-w-xl mx-auto lg:mx-0 mt-6 text-base md:text-lg leading-relaxed text-gray-600"
          >
            {description}
          </motion.p>

          {actions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap justify-center lg:justify-start gap-3 mt-8"
            >
              {actions.map((action) => (
                <a
                  key={action.label}
                  href={action.href}
                  onClick={action.onClick}
                  className={
                    action.variant === "secondary"
                      ? "inline-flex items-center justify-center gap-2 h-14 rounded-xl border-2 border-brand-dark/10 bg-white px-7 text-base font-semibold text-brand-dark no-underline hover:border-brand-teal/40 hover:text-brand-teal transition-colors"
                      : "inline-flex items-center justify-center gap-2 h-14 rounded-xl bg-brand-teal px-7 text-base font-semibold text-white no-underline shadow-lg shadow-brand-teal/25 hover:bg-brand-teal-dark transition-colors"
                  }
                >
                  {action.label}
                  {action.icon}
                </a>
              ))}
            </motion.div>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.25, ease: "easeOut" }}
          className="relative order-1 lg:order-2 lg:-mr-10 xl:-mr-20"
        >
          <div className="pointer-events-none absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-brand-teal/20 to-brand-green/20 blur-2xl" />
          <img src={image} alt={imageAlt} className="relative w-full rounded-3xl shadow-2xl" />

          {badges.map((badge, i) => (
            <motion.div
              key={badge.text}
              initial={{ opacity: 0, y: badge.position === "top" ? -12 : 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + i * 0.1, type: "spring", stiffness: 260 }}
              className={
                badge.position === "top"
                  ? "hidden sm:flex absolute -top-5 -left-5 items-center gap-2 rounded-full bg-white px-5 py-2.5 shadow-xl border border-black/5"
                  : "absolute -bottom-5 left-1/2 -translate-x-1/2 sm:left-auto sm:right-6 sm:translate-x-0 flex items-center gap-2 rounded-full bg-white px-5 py-2.5 shadow-xl border border-black/5"
              }
            >
              <i className={`fa ${badge.icon} ${badge.iconColor || "text-brand-teal"}`} />
              <span className="text-sm font-semibold text-brand-dark whitespace-nowrap">
                {badge.text}
              </span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

export default Hero;
